import React, { useEffect, useState } from 'react';
import { CircularProgress, Tab, Tabs, Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { rejectCodesToBeClearedInStaff, policyType } from '@ourbranch/lookups';
import { getFarmPolicyMask } from 'common/helpers/farm-helpers';

import { track } from 'core/helpers/analytics';
import { Card } from 'core/components/card';
import { OfferPageTabs } from 'core/store/offer-store';
import { useStore } from 'core/store/store.mobx';
import useSession from 'core/hooks/use-session';
import { useToast } from 'core/components/toast';
import Notification from 'core/helpers/notifications';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  REJECTIONS_NAV_ANCHOR
} from 'core/components/validation-errors-modal';
import { UDRModal } from 'core/components/unverifiable-driving-record-modal';
import { useScroll } from 'core/hooks/use-scroll';
import { OfferNotifications } from 'offer/components/notifications';
import { RecalculatingDim } from './components/recalculating-dim';
import { PurchasedOfferBanner } from './components/purchased-offer-banner';
import ResetUWFormModal from './components/pre-bind-UW/components/common/reset-UW-form-modal';
import ConnectedHomeDiscountModal from './components/sidebar/components/discounts-card/connected-home-discount/connected-home-discount-modal';
import BranchPlusMultiPolicyDiscountModal from './components/sidebar/components/discounts-card/branch-plus-multi-policy-discount/branch-plus-multi-policy-discount-modal';
import { FarmAssociationDiscountModal } from './components/sidebar/components/discounts-card/farm-discount';
import { AutoDetails } from './components/auto-details';
import { FarmDetails } from './components/farm-details';
import { OfferDialog, CustomerSearchDialog, SearchReferralDialog } from './components/dialog';
import { Footer } from './components/footer';
import { HomeDetails } from './components/home-details';
import { PeopleDetails } from './components/people-details';
import { Settings } from './components/settings';
import { PersonalInjuryProtection } from 'common/components/personal-injury-protection';
import { RaterInterstitialModal } from './components/rater-interstitial-modal';
import { OfferDifferenceModal } from './components/modal-offer-diff';
import { OfferRedirectInterstitial } from './components/offer-redirect-interstitial';
import { RentersOffer } from 'common/components/renters';
import { CondoOfferDetails } from 'common/components/condo';
import useStyles from './offer.styles';

const Offer = observer(() => {
  const classes = useStyles();
  const toast = useToast();
  const { offer: store, affinityLookups } = useStore();
  const { fetchAffinityData } = store;
  const { dirty, values, validateForm, setFieldValue, setFieldTouched, initialValues } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const [showUWResetModal, setShowUWResetModal] = useState(false);
  const [hasDisabledAffinity, setHasDisabledAffinity] = useState(false);
  const { viewOnly, user } = useSession();

  // Page-level scroll handler so go-to-section works for every tab. Per-tab components mount/unmount
  // with their tab, so a target on a not-yet-active tab (e.g. renters on AUTO) had nothing listening;
  // this instance is always mounted and useScroll retries until the switched-to tab mounts the anchor.
  useScroll(store?.scrollTo);

  const notificationTypes = store?.notifications?.map((i) => i).reduce((curr, prev) => prev.concat(curr));
  const shouldClearRejectCodes = rejectCodesToBeClearedInStaff.includes(
    store.offer?.quote?.offerings?.homeownersRejectCode
  );

  useEffect(() => {
    store.setFormAction({ dirty, stale: store.isStale, outDated: store.isOutdated });
    // if the validation schema changes *after* the validation has run we have call validate again manually
    validateForm();
  }, [dirty, store.setFormAction, validateForm, store.isStale, store.isOutdated]);

  useEffect(() => {
    // set to auto here if ineligible for home but form hasn't updated yet
    if (
      store.priorQuoteWithPreBindUWRejections.ineligibleForHome &&
      values.selectedOption &&
      values.selectedOption.includes(policyType.Home)
    ) {
      store.setSelectedOption(policyType.Auto);
      setFieldValue('selectedOption', policyType.Auto);
      setFieldTouched('selectedOption', true);
    }
  }, [
    values.selectedOption,
    store.selectedOption,
    store.priorQuoteWithPreBindUWRejections.ineligibleForHome,
    store,
    setFieldTouched,
    setFieldValue
  ]);

  useEffect(() => {
    // auto scroll up so notifications are in view
    if (
      store.notifications?.length &&
      notificationTypes !== Notification.Offer.NewConnectedHomeSignUpCustomer &&
      !store.loading
    ) {
      document.getElementById('notificationsContainer').scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [store.notifications, store.loading, notificationTypes]);

  // if a tab is selected and then the insurance type is changed to no longer show that tab, reset the current tab to people
  useEffect(() => {
    const shouldSwitchToPeopleTab =
      (store.currentTab === OfferPageTabs.HOME && !store.shouldShowHomeTab) ||
      (store.currentTab === OfferPageTabs.AUTO && !store.shouldShowAutoTab) ||
      (store.currentTab === OfferPageTabs.RENTERS && !store.shouldShowRentersTab) ||
      (store.currentTab === OfferPageTabs.PIP && !store.shouldShowPIPTab) ||
      (store.currentTab === OfferPageTabs.FARM && !store.shouldShowFarmTab);

    if (shouldSwitchToPeopleTab) {
      store.setCurrentTab(OfferPageTabs.PEOPLE);
    }
  }, [
    store.currentTab,
    store.shouldShowHomeTab,
    store.shouldShowAutoTab,
    store.shouldShowRentersTab,
    store.shouldShowPIPTab,
    store.setCurrentTab,
    store
  ]);

  // Clear whitelisted reject codes if exists
  useEffect(() => {
    const clearRejectCodes = async () => {
      await store.clearRejectCodes();
      if (store.areRejectCodesCleared) {
        toast.notify({
          type: 'success',
          message:
            'This offer is for a home located in a wind/hail exclusion zone. Please ensure the member understands what this means for their coverage, and assist them with purchasing the excluded coverage through one of our partners, if applicable.',
          durationInSeconds: 15,
          label: 'X',
          action: () => {}
        });
      }
    };

    if (shouldClearRejectCodes) {
      clearRejectCodes();
    }
  }, [shouldClearRejectCodes, store.areRejectCodesCleared, store, toast]);

  useEffect(() => {
    fetchAffinityData(user.affinityCode);
  }, []);

  useEffect(() => {
    if (store.recalculatingPrice && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [store.recalculatingPrice]);

  useEffect(() => {
    const affinityData = affinityLookups.data?.find(({ affinity }) => affinity === user.affinityCode);

    if (affinityData?.disabled) {
      setHasDisabledAffinity(true);
    }
  }, [affinityLookups?.data, user.affinityCode]);

  const primaryChanged =
    values?.drivers?.find((driver) => driver.isPrimary)?.id !==
    initialValues?.drivers?.find((driver) => driver.isPrimary)?.id;

  const goToPIPCoverageSection = () => {
    store.setScrollTo('personalInjuryProtectionSection');
    store.setCurrentTab(OfferPageTabs.PIP);
  };

  // A target tab is "reachable" only if it's currently rendered. Tabs are conditionally shown by
  // policy type / eligibility (shouldShow*Tab); PEOPLE and SETTINGS/billing are always present.
  // Used to avoid switching to a tab that won't mount (which would leave a blank page) — see B9.
  const TAB_AVAILABILITY = {
    [OfferPageTabs.HOME]: () => store.shouldShowHomeTab,
    [OfferPageTabs.CONDO]: () => store.shouldShowCondoTab,
    [OfferPageTabs.AUTO]: () => store.shouldShowAutoTab,
    [OfferPageTabs.RENTERS]: () => store.shouldShowRentersTab,
    [OfferPageTabs.FARM]: () => store.shouldShowFarmTab,
    [OfferPageTabs.PIP]: () => store.shouldShowPIPTab
  };
  const isTabReachable = (tab) => {
    const check = TAB_AVAILABILITY[OfferPageTabs[tab]];
    return check ? check() : true;
  };

  // Validation Errors modal "go to section" (#30230). Closes the modal and sends the form to the
  // section behind it. `target` is the resolved nav target from the modal ({ anchor, tab }); the
  // tab is switched first so the anchor is mounted, then setScrollTo drives useScroll (scroll +
  // flash). Mirrors goToPIPCoverageSection above.
  const handleGoToSection = ({ anchor, tab }) => {
    track('Staff Validation Error Go To Section Clicked', { section: anchor, flow: 'offer' });
    // B9 (designer PR feedback, Jun 16): when a home is rejected/ineligible it's deselected and the
    // HOME tab unmounts, but an escrow mortgage error can still show a "Mortgage details" card. Its
    // target tab (HOME) is then unreachable — switching to it + scrolling would land on a blank
    // page. In that case skip the dead tab switch and send the user to the rejections block in the
    // sidebar (always mounted), which is what they actually need to resolve.
    if (tab && !isTabReachable(tab)) {
      store.setScrollTo(REJECTIONS_NAV_ANCHOR);
      setShowValidationErrorsModal(false);
      return;
    }
    if (tab) {
      store.setCurrentTab(OfferPageTabs[tab]);
    }
    store.setScrollTo(anchor);
    setShowValidationErrorsModal(false);
  };

  useEffect(() => {
    if (
      store.state === 'MI' &&
      ((values?.nonDrivers || [])?.concat(values?.drivers)?.length >
        (initialValues?.nonDrivers || [])?.concat(initialValues?.drivers)?.length ||
        primaryChanged)
    ) {
      setFieldValue('auto.pipEligibilityConfirmation', false);
      toast.notify({
        type: 'success',
        message: `The ${primaryChanged ? 'primary applicant' : 'list of household residents'} has been updated. Please verify that the policy is still eligible for the selected PIP coverage.`,
        label: 'See PIP coverage',
        action: () => goToPIPCoverageSection()
      });
    }
  }, [values?.drivers?.length, values?.nonDrivers?.length, primaryChanged]);

  return (
    <div
      className={classNames(
        classes.pageContainer,
        { [classes.container]: store.errors.length === 0 },
        { [classes.withErrors]: store.errors.length > 0 }
      )}
    >
      {store.recalculatingPrice && (
        <div className={classes.recalculatingOverlay} data-testid="recalculating-overlay">
          <CircularProgress className={classes.recalculatingLoader} size={48} />
        </div>
      )}
      {/* Modal priority: the auto-redirect interstitial takes precedence over the merge modal */}
      {store.redirectTarget && <OfferRedirectInterstitial />}
      {!store.redirectTarget && store.showRaterInterstitialModal && (
        <RaterInterstitialModal open={store.showRaterInterstitialModal} />
      )}
      {store.offerDifference && <OfferDifferenceModal open={store.offerDifference} />}
      {store.notifications?.length && notificationTypes !== 'NewConnectedHomeSignUpCustomer' && (
        <OfferNotifications setShowUWResetModal={setShowUWResetModal} />
      )}
      {store.showBranchPlusMultiPolicyDiscountModal && <BranchPlusMultiPolicyDiscountModal />}
      {store.showConectedHomeModal && <ConnectedHomeDiscountModal />}
      {store.showFarmAssociationModal && <FarmAssociationDiscountModal />}
      {showUWResetModal && <ResetUWFormModal setShowUWResetModal={setShowUWResetModal} />}
      {/* Warn when this offer was already committed to a policy (#31622) */}
      <PurchasedOfferBanner />
      <RecalculatingDim>
        <Tabs
          id="offer_tabs"
          value={store.currentTab}
          variant="fullWidth"
          onChange={(_, newTab) => {
            store.setCurrentTab(newTab);
            track('Staff Offer Tab Clicked', { tab: newTab });
          }}
        >
          <Tab id="people_tab" value={OfferPageTabs.PEOPLE} label="People" className={classes.tab} />
          {store.shouldShowHomeTab && (
            <Tab id="home_details_tab" value={OfferPageTabs.HOME} label="Home Details" className={classes.tab} />
          )}
          {store.shouldShowRentersTab && (
            <Tab
              id="renters_details_tab"
              value={OfferPageTabs.RENTERS}
              label="Renters Details"
              className={classes.tab}
            />
          )}
          {store.shouldShowCondoTab && (
            <Tab id="condo_details_tab" value={OfferPageTabs.CONDO} label="Condo Details" className={classes.tab} />
          )}
          {store.shouldShowFarmTab && (
            <Tab
              id="farm_details_tab"
              value={OfferPageTabs.FARM}
              label={getFarmPolicyMask(store.selectedOption, store.state)?.tabLabel || 'Farm Details'}
              className={classes.tab}
            />
          )}
          {store.shouldShowAutoTab && (
            <Tab id="auto_details_tab" value={OfferPageTabs.AUTO} label="Auto Details" className={classes.tab} />
          )}
          {store.shouldShowPIPTab && <Tab id="pip_tab" value={OfferPageTabs.PIP} label="PIP" className={classes.tab} />}
          <Tab id="billing_tab" value={OfferPageTabs.SETTINGS} label="Billing" className={classes.tab} />
        </Tabs>
      </RecalculatingDim>

      {((store.loading && !store.recalculatingPrice) || !store.offer) && (
        <Card className={classes.cardLoadingContainer}>
          <Grid container className={classes.loaderContainer} alignItems="center" justifyContent="center">
            <CircularProgress className={classes.loader} />
          </Grid>
        </Card>
      )}
      {store.offer && (!store.loading || store.recalculatingPrice) && (
        <RecalculatingDim>
          {store.currentTab === OfferPageTabs.SETTINGS && <Settings />}
          {store.currentTab === OfferPageTabs.PEOPLE && <PeopleDetails hasAuto={store.shouldShowAutoTab} />}
          {store.currentTab === OfferPageTabs.CONDO && <CondoOfferDetails hasAuto={store.shouldShowAutoTab} />}
          {store.currentTab === OfferPageTabs.AUTO && <AutoDetails />}
          {store.currentTab === OfferPageTabs.HOME && <HomeDetails />}
          {store.currentTab === OfferPageTabs.FARM && <FarmDetails />}
          {store.currentTab === OfferPageTabs.RENTERS && <RentersOffer />}
          {store.currentTab === OfferPageTabs.PIP && <PersonalInjuryProtection />}
          {store.openDialog && <OfferDialog onClose={() => store.triggerOfferDialog(false)} />}
          <CustomerSearchDialog setShowValidationErrorsModal={setShowValidationErrorsModal} />
          {!store.offer.quote?.existingCustomer && <SearchReferralDialog />}
        </RecalculatingDim>
      )}
      {store.offer && !viewOnly && (
        <Footer
          showValidationErrorsModal={showValidationErrorsModal}
          setShowValidationErrorsModal={setShowValidationErrorsModal}
          hasDisabledAffinity={hasDisabledAffinity}
        />
      )}
      <ValidationErrorsModal
        open={showValidationErrorsModal}
        onClose={() => setShowValidationErrorsModal(false)}
        onGoToSection={handleGoToSection}
      />
      <UDRModal />
    </div>
  );
});

export default Offer;
